import React, { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import XLSX from "sheetjs-style";
import {Alert, AlertContext, Builder_, Dropdown_, FormBox, Grey_,Grey_Link, IconSvg, Main_,Main_Plain,Partial_Loader, Slider_, countries, device} from "monica-alexandria";
import { useTranslation } from "react-i18next";
import l from "../../../services/api/Landings";
import { alertMessage } from "../../../utils/messagesAlerts";
import { buildCrmImportFields, getTemplateHeader, getTemplateSampleValue, normalizeCrmImportKey, parseCrmNotesFromFile } from "../crmFileSchema";

const ImportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const StepsWrapper = styled.div`
  background: ${p=>p.theme.mid};
  border-radius: var(--smallRadius);
  padding: var(--normalPads);
`

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TemplateActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  padding-top: 1rem;
`;

const FileButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  input[type="file"] {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
  }
`;

const MappingList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;

  @media ${device.md} {
    grid-template-columns: 1fr;
  }
`;

const MappingRow = styled.div`
  display: grid;
  gap: 0.75rem;
  padding: var(--normalPads);
  background: ${(p) => p.theme.mid};
  border-radius: var(--smallRadius);
`;

const SampleValue = styled.div`
  p {
    font-size: 1.3rem;
    opacity: 0.75;
    word-break: break-word;
  }
`;

const StatsRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  min-width: 16rem;
  padding: var(--smallPads);
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.low};

  h6 {
    opacity: 0.75;
    margin-bottom: 0.4rem;
  }

  h4 {
    color: ${(p) => (p.customColor === "danger" ? "var(--warning)" : "var(--main)")};
  }
`;

const ImportFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: right;
  gap: 1rem;
  flex-wrap: wrap;

  h5 {
    opacity: 0.8;
  }
`;

const Title = styled.div`
  padding-bottom: 1rem;
`;

const CompactReview = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: var(--smallPads) var(--normalPads);
  background: ${(p) => p.theme.mid};
  border-radius: var(--smallRadius);
`;

const SectionIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding-bottom: 2rem;

  p {
    opacity: 0.8;
    line-height: 1.45;
  }
`;

const MatchColumnsWrapper = styled.div``;

const DuplicateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: var(--normalPads);
  background: ${(p) => p.theme.mid};
  border-radius: var(--smallRadius);
`;

const ResolutionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(16rem, 0.8fr) minmax(22rem, 1fr);
  gap: 1rem;
  padding: var(--normalPads);
  background: ${(p) => p.theme.low};
  border-radius: var(--smallRadius);
  align-items: center;

  @media ${device.md} {
    grid-template-columns: 1fr;
  }
`;

const ResolutionValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ResolutionBadge = styled.div`
  width: fit-content;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  background: ${(p) => p.theme.mid};
  border: 1px solid ${(p) => p.theme.high};

  p {
    font-size: 1.3rem;
    opacity: 0.85;
    word-break: break-word;
  }
`;

const ResolutionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const ResolutionCount = styled.h4`
  color: var(--main);
`;

const ResolutionHint = styled.p`
  opacity: 0.78;
  line-height: 1.4;
`;

const DuplicateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  padding: var(--normalPads);
  background: ${(p) => p.theme.low};
  border-radius: var(--smallRadius);

  @media ${device.md} {
    grid-template-columns: 1fr;
  }
`;

const DuplicateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding-bottom: 2rem;

  select {
    min-width: 26rem;
  }

  label {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
  }
`;

const DefaultColumnWrapper = styled.div`  
  padding-top: 2rem;
`

const HelpCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: var(--normalPads);
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.mid};
  border: 1px solid ${(p) => p.theme.low};
`;

const Tip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.75;
  p{
    color: ${p =>
      p.customColor === "warning"
        ? "var(--warning)"
        : p.customColor === "success"
          ? "var(--success)"
          : p.theme.color };
  }

  svg {
    width: 1.5rem;

    fill: ${p =>
      p.customColor === "warning"
        ? "var(--warning)"
        : p.customColor === "success"
          ? "var(--success)"
          : p.theme.color };
  }
`;

const ReviewHero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 2rem;
  padding: var(--normalPads);
  border-radius: var(--smallRadius);
  background:
    radial-gradient(circle at top left, ${(p) => p.variant === "success" ? "rgba(66, 198, 150, 0.10)" : "rgba(255, 181, 64, 0.12)"}, transparent 45%),
    ${(p) => p.theme.mid};
  border: 1px solid ${(p) => p.variant === "success" ? "rgba(66, 198, 150, 0.35)" : "rgba(255, 181, 64, 0.55)"};

  @media ${device.md} {
    grid-template-columns: 1fr;
  }
`;

const ReviewHeroMain = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const ReviewHeroIcon = styled.div`
  width: 4.8rem;
  height: 4.8rem;
  border-radius: 999px;
  background: ${(p) =>
    p.variant === "success"
      ? "linear-gradient(180deg, #6fd5bf 0%, #4cb89f 100%)"
      : "linear-gradient(180deg, #ffbf4d 0%, #ffab2d 100%)"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 2rem;
    fill: #fff;
  }
`;

const ReviewHeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;

  p {
    opacity: 0.9;
    line-height: 1.45;
    max-width: 72rem;
  }
`;

const ReviewHeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(14rem, 1fr));
  gap: 0;
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid ${(p) => p.theme.low};
  border-radius: var(--smallRadius);
  overflow: hidden;

  @media ${device.md} {
    grid-template-columns: 1fr 1fr;
  }
`;

const ReviewHeroStat = styled.div`
  padding: 1.6rem 2rem;
  min-width: 16rem;

  &:not(:last-child) {
    border-right: 1px solid ${(p) => p.theme.low};
  }

  h3 {
    color: ${(p) => p.variant === "success" ? "var(--main)" : "#ffbf4d"};
    margin: 0.4rem 0;
  }

  p {
    opacity: 0.78;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.2rem;

  @media ${device.md} {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  padding: var(--normalPads);
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.mid};
  border: 1px solid ${(p) => p.theme.low};
  display: flex;
  flex-direction: column;
  gap: 1rem;
  grid-column: 1 / -1;
`;

const SummaryLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;

  p {
    opacity: 0.78;
  }

  h6, h5 {
    text-align: right;
  }
`;

const ReviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  
`;

const ReviewSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    width: 2rem;
    fill: ${(p) => p.iconColor || p.theme.color};
  }

  h5 {
    color: ${(p) => p.titleColor || p.theme.color};
  }
`;

const MappingTable = styled.div`
  border: 1px solid ${(p) => p.theme.low};
  border-radius: var(--smallRadius);
  overflow: hidden;
  /* background: rgba(0, 0, 0, 0.06); */
  background: ${(p) => p.theme.mid};
`;

const MappingTableHead = styled.div`
  display: grid;
  grid-template-columns: 1fr 8rem 1fr;
  gap: 1rem;
  padding: 1.4rem 1.8rem;
  border-bottom: 1px solid ${(p) => p.theme.low};

  h6 {
    opacity: 0.8;
  }
`;

const MappingTableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 8rem 1fr;
  gap: 1rem;
  padding: 1.6rem 1.8rem;
  align-items: center;

  &:not(:last-child) {
    border-bottom: 1px solid ${(p) => p.theme.low};
  }
`;

const MatchedTableHead = styled(MappingTableHead)`
  grid-template-columns: 4.2rem 1fr 8rem 1fr;
`;

const MatchedTableRow = styled(MappingTableRow)`
  grid-template-columns: 4.2rem 1fr 8rem 1fr;
`;

const MappingStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 2.2rem;
    fill: var(--success);
  }
`;

const RoutingTableHead = styled(MappingTableHead)`
  grid-template-columns: 4.2rem 1fr 8rem 1fr;
`;

const RoutingTableRow = styled(MappingTableRow)`
  grid-template-columns: 4.2rem 1fr 8rem 1fr;
`;

const RoutingStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 2rem;
    fill: ${(p) => (p.resolved ? "var(--success)" : "var(--warning)")};
    opacity: ${(p) => (p.resolved ? 1 : 0.95)};
  }
`;

const MappingRowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const MappingRowSub = styled.p`
  opacity: 0.72;
  font-size: 1.3rem;
  line-height: 1.35;
`;

const MappingSelectCell = styled.div`
  min-width: 0;

  select {
    width: 100%;
  }
`;

const MappingArrow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  span {
    font-size: 2.6rem;
    opacity: 0.45;
  }
`;

const ReviewCase = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) 1px minmax(0, 1.15fr);
  gap: 2.4rem;
  padding: var(--normalPads);
  border-radius: var(--smallRadius);
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid ${(p) => p.theme.low};

  @media ${device.md} {
    grid-template-columns: 1fr;
    gap: 1.6rem;
  }
`;

const ReviewDivider = styled.div`
  background: ${(p) => p.theme.low};

  @media ${device.md} {
    display: none;
  }
`;

const ReviewCaseSide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const ReviewCaseIndex = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: rgba(255, 181, 64, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;

  h3 {
    color: #ffbf4d;
  }
`;

const ReviewCaseRight = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1rem;
`;




const ReviewCaseMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    width: 1.8rem;
    fill: ${(p) => p.theme.color};
    opacity: 0.8;
  }
`;

const ReviewActionLink = styled.button`
  appearance: none;
  border: 0;
  background: none;
  padding: 0;
  color: ${(p) => p.color || p.theme.color};
  font: inherit;
  text-align: left;
  cursor: pointer;
`;

export default function Crm_Import_Leads({onClose, formSchema, selectedQuestId, columnOrder, columns, onImportComplete, isStandaloneBoard = false}) {

  const { t } = useTranslation();
  const { alerts, setAlerts } = useContext(AlertContext);

  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]); // headers from the CSV file
  const [rows, setRows] = useState([]); // array of objects representing CSV rows with keys as headers
  const [mapping, setMapping] = useState({}); // { csvHeader: targetFieldKey }
  const [parsing, setParsing] = useState(false);  // to disable UI during parsing
  const [importing, setImporting] = useState(false); // to disable UI during import
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [selectedColumnId, setSelectedColumnId] = useState(
    columnOrder?.[0] || ""
  );
  const [duplicateState, setDuplicateState] = useState({
    status: "idle", // idle | checking | needs_action | resolved
    duplicates: [], // backend-provided duplicates to display
    actionByIndex: {}, // per-row action keyed by CSV row index
    applyToAll: false, // if true, applyAction overrides per-row actions
    applyAction: "skip", // default action when duplicates are found | "skip" | "update" | "keep"
    lastSignature: "", // mapping+headers signature to invalidate stale checks
  });
  const [missingCountryRows, setMissingCountryRows] = useState([]);
  const [defaultImportCountry, setDefaultImportCountry] = useState("");
  const [columnValueResolutions, setColumnValueResolutions] = useState({});
  const [routingHeader, setRoutingHeader] = useState("");


  const [activeStep, setActiveStep] = useState(1);

  // Build the list of possible target fields for mapping based on formSchema
  const targets = useMemo(() => buildCrmImportFields(formSchema, headers, { standalone: isStandaloneBoard, t }), [formSchema, headers, isStandaloneBoard, t]);

  const fieldTargets = useMemo(
    () => targets.filter((field) => field.key !== "Column"),
    [targets]
  );

  const templateHeaders = useMemo(() => {
    const seen = new Set();
    const ordered = [
      { key: "Column", label: "Column" },
      ...fieldTargets.map((field) => ({ key: field.key, label: getTemplateHeader(field) })),
    ];

    return ordered.filter((item) => {
      const normalized = normalizeCrmImportKey(item.label);
      if (!item.label || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  }, [fieldTargets]);

  const templatePreviewRow = useMemo(() => {
    const firstColumnTitle = columns?.[columnOrder?.[0]]?.title || "";
    const row = {};
    templateHeaders.forEach((item) => {
      row[item.label] = getTemplateSampleValue(item.label, firstColumnTitle);
    });
    return row;
  }, [templateHeaders, columns, columnOrder]);


  // Create lookup maps for targets to optimize mapping logic
  const targetByKey = useMemo(() => {
    const map = {};
    targets.forEach((f) => {
      map[f.key] = f;
    });
    return map;
  }, [targets]);

  // When a lead is moved between columns, we need to update the local board state immediately for responsiveness, and then call the backend to persist the change. This function handles both aspects.
  const normalizedTargetKeyByLabel = useMemo(() => {
    const map = {};
    targets.forEach((f) => {
      map[normalizeCrmImportKey(f.label)] = f.key;
      map[normalizeCrmImportKey(f.key)] = f.key;
    });
    return map;
  }, [targets]);


  const sortedCountries = useMemo(
    () => [...(countries || [])].sort((a, b) => a.country.localeCompare(b.country)),
    []
  );

 

  // Define common aliases for target fields to improve auto-mapping accuracy
  const aliasTargetKeys = useMemo(
    () => ({
      fullname: "FullName",
      name: "Name",
      firstname: "Name",
      first: "Name",
      surname: "Surname",
      lastname: "Surname",
      last: "Surname",
      email: "Email",
      mail: "Email",
      phone: "Tel",
      telephone: "Tel",
      tel: "Tel",
      mobile: "Tel",
      city: "City",
      amount: "Amount",
      column: "Column",
      stage: "Column",
      status: "Column",
      note: "Notes",
      notes: "Notes",
    }),
    []
  );

  // Auto-map CSV headers to target fields using normalized matching and aliases
  const autoMapHeaders = (csvHeaders) => {
    const next = {};
    // FileReader still has the previous render's headers; build targets from this upload.
    const available = buildCrmImportFields(formSchema, csvHeaders, { standalone: isStandaloneBoard, t });
    const byLabel = Object.fromEntries(available.flatMap(field => [
      [normalizeCrmImportKey(field.key), field.key], [normalizeCrmImportKey(field.label), field.key],
    ]));
    const keys = new Set(available.map(field => field.key));
    csvHeaders.forEach((header) => {
      const normalized = normalizeCrmImportKey(header);
      if (aliasTargetKeys[normalized] === "Column") {
        next[header] = "";
        return;
      }
      const alias = aliasTargetKeys[normalized];
      if (isStandaloneBoard && alias && keys.has(alias)) { next[header] = alias;return; }
      const direct = byLabel[normalized];
      if (direct === "Column") {
        next[header] = "";
        return;
      }
      if (direct) {
        next[header] = direct;
        return;
      }
      if (alias && keys.has(alias)) {
        next[header] = alias;
        return;
      }
      next[header] = "";
    });
    setMapping(next);
  };

  const downloadTemplate = (fileType = "xlsx") => {
    const ws = XLSX.utils.json_to_sheet([templatePreviewRow], {
      header: templateHeaders.map((item) => item.label),
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Import template");
    XLSX.writeFile(wb, `crm-import-template.${fileType}`);
  };

  const detectRoutingHeader = (csvHeaders) => {
    return (
      csvHeaders.find((header) => {
        const normalized = normalizeCrmImportKey(header);
        return aliasTargetKeys[normalized] === "Column" || normalizedTargetKeyByLabel[normalized] === "Column";
      }) || ""
    );
  };

  const resetDuplicateState = () => {
    setDuplicateState((prev) => ({ ...prev, status: "idle", duplicates: [] }));
  };

  // Handle file input change, parse the CSV/Excel file, and extract headers and rows
  const parseCsv = (file) => {
    setParsing(true);
    setFileName(file?.name || "");
    resetDuplicateState();
    setMissingCountryRows([]);
    setDefaultImportCountry("");
    setColumnValueResolutions({});
    setRoutingHeader("");

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const wb = XLSX.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
        });

        const [rawHeaders = [], ...rawRows] = raw || [];
        const cleanHeaders = rawHeaders.map((h) => String(h || "").trim());
        const mappedRows = rawRows
          .filter((row) => row && row.length)
          .map((row) => {
            const obj = {};
            cleanHeaders.forEach((h, idx) => {
              obj[h] = row?.[idx] ?? "";
            });
            return obj;
          });

        const filteredHeaders = cleanHeaders.filter(Boolean);
        setHeaders(filteredHeaders);
        setRows(mappedRows);
        setRoutingHeader(detectRoutingHeader(filteredHeaders));
        autoMapHeaders(filteredHeaders);
        setActiveStep(2);
      } catch (err) {
        console.error("CSV parse failed", err);
        alertMessage(alerts, setAlerts, t("Failed to parse CSV"), "Error");
      } finally {
        setParsing(false);
      }
    };
    reader.onerror = () => {
      setParsing(false);
      alertMessage(alerts, setAlerts, t("Failed to read file"), "Error");
    };
    reader.readAsArrayBuffer(file);
  };


  // Call backend to detect duplicates based on current mapping and CSV data
  const updateMapping = (header, targetKey) => {
    if (!targetKey) {
      setMapping((prev) => ({ ...prev, [header]: "" }));
      resetDuplicateState();
      return;
    }

    setMapping((prev) => {
      const next = { ...prev, [header]: targetKey };
      Object.keys(next).forEach((h) => {
        if (h !== header && next[h] === targetKey) {
          next[h] = "";
        }
      });
      return next;
    });
    resetDuplicateState();
  };

  // Generate a unique signature based on current mapping and headers to determine when to re-check for duplicates
  const mappingSignature = useMemo(() => {
    return JSON.stringify({mapping,headers, defaultImportCountry, });
  }, [mapping, headers, defaultImportCountry]);


  const getMappedValue = (row, targetKey) => {
    const header = headers.find((h) => mapping[h] === targetKey);
    if (!header) return "";
    return row?.[header] ?? "";
  };

  const columnOptions = useMemo(() => {
    return (columnOrder || []).map((colId) => ({
      id: colId,
      title: columns?.[colId]?.title || colId,
      normalized: normalizeCrmImportKey(columns?.[colId]?.title || colId),
    }));
  }, [columnOrder, columns]);

  const columnsByNormalized = useMemo(() => {
    const map = {};
    columnOptions.forEach((option) => {
      if (!map[option.normalized]) {
        map[option.normalized] = [];
      }
      map[option.normalized].push(option);
    });
    return map;
  }, [columnOptions]);

  const columnMappingEnabled = !!routingHeader;

  const mappedFieldPairs = useMemo(() => {
    return headers
      .filter((header) => mapping[header])
      .map((header) => ({
        csv: header,
        crm: targetByKey[mapping[header]]?.label || mapping[header],
      }));
  }, [headers, mapping, targetByKey]);

  const routingValueRows = useMemo(() => {
    if (!columnMappingEnabled || !routingHeader) {
      return { rows: [], blankCount: rows.length };
    }

    const grouped = {};
    let blankCount = 0;

    rows.forEach((row) => {
      const rawValue = String(row?.[routingHeader] || "").trim();
      if (!rawValue) {
        blankCount += 1;
        return;
      }

      const normalized = normalizeCrmImportKey(rawValue);
      const matches = columnsByNormalized[normalized] || [];
      const autoColumnId = matches.length === 1 ? matches[0].id : "";

      if (!grouped[normalized]) {
        grouped[normalized] = {
          normalized,
          rawValue,
          count: 0,
          autoColumnId,
          reason: matches.length > 1 ? "ambiguous" : matches.length === 0 ? "missing" : "matched",
        };
      }

      grouped[normalized].count += 1;
    });

    const valueRows = Object.values(grouped).map((item) => {
      const manualColumnId = columnValueResolutions[item.normalized];
      const resolvedColumnId = manualColumnId || item.autoColumnId || "";

      return {
        ...item,
        resolvedColumnId,
        needsReview: !resolvedColumnId,
      };
    });

    return { rows: valueRows, blankCount };
  }, [columnMappingEnabled, routingHeader, rows, columnsByNormalized, columnValueResolutions]);

  const columnImportAnalysis = useMemo(() => {
    return {
      matchedRows: routingValueRows.rows
        .filter((item) => item.resolvedColumnId)
        .reduce((sum, item) => sum + item.count, 0),
      defaultRows: routingValueRows.blankCount,
      unresolved: routingValueRows.rows.filter((item) => item.needsReview),
    };
  }, [routingValueRows]);

  const hasColumnIssue = columnImportAnalysis.unresolved.length > 0;

  const resolveColumnIdForRow = (row) => {
    if (!columnMappingEnabled || !routingHeader) return selectedColumnId;

    const rawValue = String(row?.[routingHeader] || "").trim();
    if (!rawValue) return selectedColumnId;

    const normalized = normalizeCrmImportKey(rawValue);
    const manualColumnId = columnValueResolutions[normalized];
    if (manualColumnId) return manualColumnId;

    const matches = columnsByNormalized[normalized] || [];
    if (matches.length === 1) return matches[0].id;

    return "";
  };



  // Build the payload for each CSV row based on the mapping and target field definitions, including handling of custom fields
  const detectDuplicates = async () => {
    if (!selectedQuestId || !rows.length || !headers.length) {
      return [];
    }

    const emailMapped = headers.some((h) => mapping[h] === "Email");
    const telMapped = headers.some((h) => mapping[h] === "Tel");
    if (!emailMapped && !telMapped) {
      return [];
    }

    const payloadRows = rows.map((row) => buildRowPayload(row));

    const res = await l.detectDuplicates({
      landingId: selectedQuestId,
      rows: payloadRows,
    });

    return res?.data?.duplicates || [];
  };

  
  // Build the payload for each CSV row based on the mapping and target field definitions, including handling of custom fields
  const buildRowPayload = (row) => {
    const payload = {};
    const customFields = [];
    const normalizeCountryCode = (value) => {
      const raw = String(value || "").trim();
      if (!raw) return "";
      if (raw.startsWith("+")) return raw;
      if (raw.startsWith("00")) return `+${raw.slice(2)}`;
      if (/^\d+$/.test(raw)) return `+${raw}`;
      return "";
    };

    headers.forEach((header) => {
      const targetKey = mapping[header];
      if (!targetKey) return;
      const value = row?.[header];
      if (value === null || value === undefined || String(value).trim() === "")
        return;

      if (targetKey === "Amount") {
        payload.Amount = String(value);
        return;
      }
      if (targetKey === "Country") {
        payload.Country = String(value);
        return;
      }
      if (targetKey === "Column") {
        return;
      }
      if (targetKey === "Notes") {
        payload.Notes = parseCrmNotesFromFile(value);
        return;
      }

      const targetField = targetByKey[targetKey];
      if (!targetField) return;

      if (targetField.isCustom) {
        customFields.push({ key: targetField.label, value });
      } else {
        payload[targetField.key] = value;
      }
    });

    if (customFields.length) {
      payload.CustomField = customFields;
    }
    if (!payload.Country && defaultImportCountry) {
      payload.Country = defaultImportCountry;
    }
    if (payload.Tel) {
      const telRaw = String(payload.Tel).replace(/\s+/g, "");
      const countryRaw = String(payload.Country || "").trim();
      const hasPhoneCountryCode = telRaw.startsWith("+") || telRaw.startsWith("00");

      if (hasPhoneCountryCode) {
        if (telRaw.startsWith("00")) {
          payload.Tel = `+${telRaw.slice(2)}`;
        } else {
          payload.Tel = telRaw;
        }
      } else {
        const explicitCountryCode = normalizeCountryCode(countryRaw);
        const defaultCountryCode = !countryRaw
          ? normalizeCountryCode(defaultImportCountry)
          : "";
        const countryPrefix = explicitCountryCode || defaultCountryCode;
        const countryDigits = countryPrefix.replace(/^\+/, "");

        if (countryPrefix) {
          if (telRaw.startsWith(countryDigits)) {
            payload.Tel = telRaw;
          } else {
            payload.Tel = `${countryPrefix}${telRaw}`;
          }
        }
      }
    }
    return payload;
  };


  // Call backend to detect duplicates based on current mapping and CSV data
  const duplicatesReady = useMemo(() => {
    if (!duplicateState.duplicates.length) return true;
    if (duplicateState.applyToAll) return true;
    return duplicateState.duplicates.every(
      (dup) => duplicateState.actionByIndex[dup.index]
    );
  }, [duplicateState]);



  const hasAnyMapping = useMemo(() => Object.values(mapping).some(Boolean), [mapping]);
  const hasCountryIssue = missingCountryRows.length > 0;
  const hasDuplicateIssue = duplicateState.duplicates.length > 0;
  const hasAnyIssue = hasCountryIssue || hasDuplicateIssue || hasColumnIssue;



  // Check if the payload for a given row is effectively empty (i.e., all mapped fields are blank), which can be used to skip such rows during import
  const isRowEmpty = (payload) => {
    const systemKeys = Object.keys(payload).filter(
      (k) => !["CustomField"].includes(k)
    );
    const hasSystemValue = systemKeys.some((k) => {
      const v = payload[k];
      return typeof v === "string" ? v.trim() !== "" : !!v;
    });
    const hasCustomValue =
      payload.CustomField && payload.CustomField.some((f) => f.value);
    return !(hasSystemValue || hasCustomValue);
  };

  // Reset duplicate check state when mapping or headers change to ensure users re-validate with the backend if they modify the mapping after seeing duplicates
  const validateImport = () => {
    if (!selectedQuestId) {
      alertMessage(alerts, setAlerts, t("Select a quest first"), "Error");
      return false;
    }

    if (!rows.length) {
      alertMessage(alerts, setAlerts, t("No rows to import"), "Error");
      return false;
    }

    if (!selectedColumnId) {
      alertMessage(alerts, setAlerts, t("Select a default column"), "Error");
      return false;
    }

    if (!hasAnyMapping) {
      alertMessage(alerts, setAlerts, t("Map at least one field"), "Error");
      return false;
    }

    return true;
  };

  const handleNextStep = () => {
    if (activeStep === 1 && !rows.length) {
      alertMessage(alerts, setAlerts, t("Upload a file first"), "Error");
      return;
    }
    if (activeStep === 2 && hasColumnIssue) {
      alertMessage(alerts, setAlerts, t("Resolve unmatched column values before continuing"), "Warning");
      return;
    }
    if (activeStep === 3 && !hasAnyMapping) {
      alertMessage(alerts, setAlerts, t("Map at least one field"), "Error");
      return;
    }
    setActiveStep((prev) => Math.min(4, prev + 1));
  };


  const handleBackStep = () => {
    if (activeStep === 1) {
      onClose();
      return;
    }
    setActiveStep((prev) => Math.max(1, prev - 1));
  };


  const getMissingCountryRows = () => {
    const missingCountry = [];
    rows.forEach((row, idx) => {
      const telValue = String(getMappedValue(row, "Tel") || "").trim();
      if (!telValue) return;
      const hasCountryCode = telValue.startsWith("+") || telValue.startsWith("00");
      const countryValue = String(getMappedValue(row, "Country") || "").trim();
      if (!hasCountryCode && !countryValue && !defaultImportCountry) {
        missingCountry.push(idx);
      }
    });
    return missingCountry;
  };

  const ensureDuplicateStateIsCurrent = async () => {
    if (duplicateState.lastSignature !== mappingSignature) {
      setDuplicateState((prev) => ({
        ...prev,
        status: "idle",
        duplicates: [],
        lastSignature: mappingSignature,
      }));
    }

    if (
      duplicateState.status === "idle" ||
      duplicateState.lastSignature !== mappingSignature
    ) {
      setDuplicateState((prev) => ({ ...prev, status: "checking" }));
      const duplicates = await detectDuplicates();

      if (duplicates.length) {
        const initialActions = {};
        duplicates.forEach((dup) => {
          initialActions[dup.index] = "skip";
        });

        setDuplicateState({
          status: "needs_action",
          duplicates,
          actionByIndex: initialActions,
          applyToAll: false,
          applyAction: "skip",
          lastSignature: mappingSignature,
        });
        alertMessage( alerts,setAlerts,t("Duplicates found. Choose how to handle them."), "Warning" );
        return false;
      }

      setDuplicateState((prev) => ({
        ...prev,
        status: "resolved",
        duplicates: [],
        lastSignature: mappingSignature,
      }));
    }

    return true;
  };


  const preparePayloadRows = () => {
    return rows
      .map((row, index) => ({
        index,
        columnId: resolveColumnIdForRow(row),
        payload: buildRowPayload(row),
      }))
      .filter(({ payload }) => !isRowEmpty(payload));
  };



  const submitImport = async (payloadRows) => {
    setImporting(true);
    setImportProgress({done: 0, total: payloadRows.length });

    try {
      const groupedRows = payloadRows.reduce((acc, item) => {
        const key = item.columnId || selectedColumnId;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});

      const totals = { created: 0, updated: 0, failed: 0, skipped: 0 };
      let processed = 0;

      for (const [columnId, group] of Object.entries(groupedRows)) {
        const actions = {};
        if (!duplicateState.applyToAll) {
          group.forEach((item, batchIndex) => {
            const action = duplicateState.actionByIndex[item.index];
            if (action) {
              actions[batchIndex] = action;
            }
          });
        }

        const res = await l.importLeads({
          landingId: selectedQuestId,
          columnId,
          rows: group.map((item) => item.payload),
          duplicateStrategy: {
            default: duplicateState.applyAction || "skip",
            actions: duplicateState.applyToAll ? {} : actions,
          },
        });

        if (!res) {
          throw new Error("Import request failed");
        }

        const result = res?.data || {};
        totals.created += result.created || 0;
        totals.updated += result.updated || 0;
        totals.failed += result.failed || 0;
        totals.skipped += result.skipped || 0;
        processed += group.length;
        setImportProgress({ done: processed, total: payloadRows.length });
      }

      setImporting(false);

      alertMessage(
        alerts,
        setAlerts,
        t("Imported {{count}} leads", {count: (totals.created || 0) + (totals.updated || 0) }),
        totals.failed ? "Warning" : "Success"
      );

      if (totals.skipped) {
        alertMessage(alerts, setAlerts, t("Skipped {{count}} duplicates", {count: totals.skipped }), "Warning");
      }

      if (onImportComplete) onImportComplete();
      onClose();
    } catch (err) {
      console.error(err);
      setImporting(false);
      alertMessage(alerts, setAlerts, t("Import failed"), "Error");
    }
  };

  // Handle the main import action, including validation, duplicate handling, payload construction, and API call to perform the import
  const handleImport = async () => {
    if (!validateImport()) return;

    const missingCountry = getMissingCountryRows();
    if (missingCountry.length && !defaultImportCountry) {
      setMissingCountryRows(missingCountry);
      alertMessage(alerts, setAlerts, t("Country code is required for some phone numbers."), "Warning" );
      return;
    }
    if (!missingCountry.length && missingCountryRows.length) {
      setMissingCountryRows([]);
    }

    const canContinue = await ensureDuplicateStateIsCurrent();
    if (!canContinue) return;

    if (!duplicatesReady) {
      alertMessage( alerts,setAlerts, t("Choose an action for all duplicates"),"Warning" );
      return;
    }

    const payloadRows = preparePayloadRows();
    if (!payloadRows.length) {
      alertMessage(alerts, setAlerts, t("No valid rows to import"), "Warning");
      return;
    }

    await submitImport(payloadRows);
  };


    useEffect(() => {
    if (!selectedColumnId && columnOrder?.length) {
      setSelectedColumnId(columnOrder[0]);
    }
  }, [columnOrder, selectedColumnId]);
 
  return (
    <Slider_
      title={t("Import CSV")}
      text={activeStep === 4 ? t("Import") : t("Next")}
      grey={activeStep === 1 ? t("Cancel") : t("Back")}
      iconGreyLeft={activeStep === 1 ? "Xicon" : "Back"}
      onClick={activeStep === 4 ? handleImport : handleNextStep}
      onGrey={handleBackStep}
      onClose={onClose}
    >
      <Alert />

      {importing ? (
        <Partial_Loader />
      ) : (
        <ImportWrapper>
          <StepsWrapper> <h5>{t("Step {{current}} of {{total}}", { current: activeStep, total: 4 })} </h5></StepsWrapper>
          {activeStep === 1 && (
            <>
              <FormBox>
                <SectionIntro>
                  <h6>{t("Upload file")}</h6>
                  <p>{t("Upload a CSV or Excel file with the leads you want to import.")}</p>
                </SectionIntro>
                <FileRow>
                  <FileButtonWrapper>
                    <Main_ iconLeft="Upload" text={t("Choose file")} size="small" />
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) parseCsv(file);
                        e.target.value = "";
                      }}
                    />
                  </FileButtonWrapper>
                  <p>{fileName || t("No file selected")}</p>
                </FileRow>
                {parsing && <Partial_Loader />}
              </FormBox>

              <HelpCard>
                <h6>{t("Template guide")}</h6>
                <TemplateActions>
                  <Grey_Link
                    text={t("Download XLSX template")}
                    iconLeft="Download"
                    size="small"
                    onClick={() => downloadTemplate("xlsx")}
                  />
                  <Grey_Link
                    text={t("Download CSV template")}
                    iconLeft="Download"
                    size="small"
                    onClick={() => downloadTemplate("csv")}
                  />
                </TemplateActions>
                <Tip>
                  <IconSvg Icon="Info" />
                  <p>{t("`Column`: use an existing CRM board column name. If blank, the default column will be used.")}</p>
                </Tip>
                <Tip>
                  <IconSvg Icon="Info" />
                  <p>{t("`Tel`: include the country code, for example `+306912345678`.")}</p>
                </Tip>
                <Tip>
                  <IconSvg Icon="Info" />
                  <p>{t("`Notes`: use one note per line inside the same cell.")}</p>
                </Tip>
                <Tip>
                  <IconSvg Icon="Info" />
                  <p>{t("Optional for notes: start a line with `DD/MM/YYYY:` if you want to keep the date in the note text.")}</p>
                </Tip>
              </HelpCard>
            </>
          )}

          {activeStep === 2 && headers.length === 0 && (
            <DuplicateSection>
              <h6>{t("No file parsed yet")}</h6>
              <p>{t("Go back and upload a valid CSV/XLSX file first.")}</p>
            </DuplicateSection>
          )}

          {activeStep === 2 && headers.length > 0 && (
            <>
              {columnMappingEnabled ? (
                <>
                  <SectionIntro>
                    <h2>{t("Match board columns")}</h2>
                    <p>{t("Choose which CRM column each value from your file should go to.")}</p>
                  </SectionIntro>
              
                  <ReviewSection>
                    <ReviewSectionHeader
                      iconColor={hasColumnIssue ? "var(--warning)" : "var(--success)"}
                      titleColor={hasColumnIssue ? "var(--warning)" : undefined}
                    >
                      <IconSvg Icon={hasColumnIssue ? "Warning" : "Check"} />
                      <h5>{t("Match board columns")}</h5>
                    </ReviewSectionHeader>
                    <MappingTable>
                      <RoutingTableHead>
                        <div />
                        <h5>{t("File value")}</h5>
                        <div />
                        <h5>{t("CRM column")}</h5>
                      </RoutingTableHead>
                      {routingValueRows.rows.map((item) => (
                        <RoutingTableRow key={item.normalized}>
                          <RoutingStatus resolved={!!item.resolvedColumnId}>
                            <IconSvg Icon={item.resolvedColumnId ? "Check" : "Warning"} />
                          </RoutingStatus>
                          <MappingRowMain>
                            <h5>{item.rawValue}</h5>
                            <MappingRowSub>
                              {t("{{count}} row affected", { count: item.count })}
                              {item.count > 1 ? "s" : ""}
                              {item.needsReview
                                ? ` • ${item.reason === "ambiguous"
                                    ? t("matches more than one CRM column")
                                    : t("no CRM column match yet")}`
                                : ` • ${t("matched automatically")}`}
                            </MappingRowSub>
                          </MappingRowMain>
                          <MappingArrow>
                            <span>&rarr;</span>
                          </MappingArrow>
                          <MappingSelectCell>
                            <Dropdown_ label={t("Import into CRM column")}>
                              <select
                                value={item.resolvedColumnId}
                                onChange={(e) =>
                                  setColumnValueResolutions((prev) => ({
                                    ...prev,
                                    [item.normalized]: e.target.value,
                                  }))
                                }
                              >
                                <option value="">{t("Select existing CRM column")}</option>
                                {columnOrder?.map((colId) => (
                                  <option key={colId} value={colId}>
                                    {columns?.[colId]?.title || colId}
                                  </option>
                                ))}
                              </select>
                            </Dropdown_>
                          </MappingSelectCell>
                        </RoutingTableRow>
                      ))}
                    </MappingTable>
                    {columnImportAnalysis.defaultRows > 0 ? (
                      <Tip>
                        <IconSvg Icon="Info" />
                        <p>{t("{{count}} row without a value in `{{header}}` will go to the default column.", { count: columnImportAnalysis.defaultRows, header: routingHeader })}</p>
                      </Tip>
                    ) : null}
                  <DefaultColumnWrapper>
                    <Builder_ title={t("Default column")}>
                      <Dropdown_ label={t("Fallback for rows without a valid board column")}>
                        <select
                          value={selectedColumnId}
                          onChange={(e) => setSelectedColumnId(e.target.value)}
                        >
                          {columnOrder?.map((colId) => (
                            <option key={colId} value={colId}>
                              {columns?.[colId]?.title || colId}
                            </option>
                          ))}
                        </select>
                      </Dropdown_>
                    </Builder_>
                  </DefaultColumnWrapper>
                  </ReviewSection>
                </>
              ) : (
                <DuplicateSection>
                  <SectionIntro>
                    <h6>{t("No board column field found in file")}</h6>
                    <p>{t("Your file does not include a `Column` field. All imported leads will go to the default column you selected above.")}</p>
                  </SectionIntro>
                </DuplicateSection>
              )}
            </>
          )}

          {activeStep === 3 && headers.length > 0 && (
            <MatchColumnsWrapper>
              <SectionIntro>
                <h3>{t("Match form fields")}</h3>
                <p>{t("Map the remaining file columns to your lead form fields.")}</p>
              </SectionIntro>
              <MappingTable>
                <MappingTableHead>
                  <h6>{t("File column")}</h6>
                  <div />
                  <h6>{t("CRM field")}</h6>
                </MappingTableHead>
                {headers
                  .filter((header) => header !== routingHeader)
                  .map((header) => (
                    <MappingTableRow key={header}>
                      <MappingRowMain>
                        <h5>{header}</h5>
                        <MappingRowSub>
                          {t("Sample")}: {String(rows?.[0]?.[header] || "")}
                        </MappingRowSub>
                      </MappingRowMain>
                      <MappingArrow>
                        <span>&rarr;</span>
                      </MappingArrow>
                      <MappingSelectCell>
                        <Dropdown_ label={t("Map to field")}>
                          <select
                            value={mapping[header] || ""}
                            onChange={(e) => updateMapping(header, e.target.value)}
                          >
                            <option value="">{t("Ignore")}</option>
                            {fieldTargets.map((field) => (
                              <option key={field.key} value={field.key}>
                                {field.label}
                              </option>
                            ))}
                          </select>
                        </Dropdown_>
                      </MappingSelectCell>
                    </MappingTableRow>
                  ))}
              </MappingTable>
            </MatchColumnsWrapper>
          )}

          {activeStep === 4 && (
            <ReviewHero variant={(hasDuplicateIssue || hasCountryIssue) ? "warning" : "success"}>
              <ReviewHeroMain>
                <ReviewHeroIcon variant={(hasDuplicateIssue || hasCountryIssue) ? "warning" : "success"}>
                  <IconSvg Icon={(hasDuplicateIssue || hasCountryIssue) ? "Warning" : "Check"} />
                </ReviewHeroIcon>
                <ReviewHeroText>
                  <h3>
                    {(hasDuplicateIssue || hasCountryIssue)
                      ? t("Review required before import")
                      : t("Everything is ready to import")}
                  </h3>
                  <p>
                    {(hasDuplicateIssue || hasCountryIssue)
                      ? t("Run through the checks below and resolve anything blocking the import.")
                      : t("Your file routing and field mapping are complete. You can import now.")}
                  </p>
                </ReviewHeroText>
              </ReviewHeroMain>
              <ReviewHeroStats>
                <ReviewHeroStat variant={(hasDuplicateIssue || hasCountryIssue) ? "warning" : "success"}>
                  <p>{t("Rows")}</p>
                  <h3>{rows.length}</h3>
                  <p>{t("Ready to import")}</p>
                </ReviewHeroStat>
                <ReviewHeroStat variant={(hasDuplicateIssue || hasCountryIssue) ? "warning" : "success"}>
                  <p>{t("Mapped fields")}</p>
                  <h3>{mappedFieldPairs.length}</h3>
                  <p>{t("File fields matched")}</p>
                </ReviewHeroStat>
              </ReviewHeroStats>
            </ReviewHero>
          )}

          {activeStep === 4 && (
            <SummaryGrid>
              <SummaryCard>
                <h5>{t("Field mapping summary")}</h5>
                <SummaryLine>
                  <p>{t("Mapped file fields")}</p>
                  <h6>{mappedFieldPairs.length}</h6>
                </SummaryLine>
                <SummaryLine>
                  <p>{t("Imported fields")}</p>
                  <h5>
                    {mappedFieldPairs.length
                      ? mappedFieldPairs.map((item) => item.crm).join(", ")
                      : t("No fields mapped")}
                  </h5>
                </SummaryLine>
              </SummaryCard>
            </SummaryGrid>
          )}

          {activeStep === 4 && duplicateState.status === "checking" && (
            <DuplicateSection>
              <h6>{t("Checking duplicates...")}</h6>
              <Partial_Loader />
            </DuplicateSection>
          )}

          {activeStep === 4 && duplicateState.duplicates.length > 0 && (
            <DuplicateSection>
              <h6>{t("Action required")}: {duplicateState.duplicates.length}</h6>
              <DuplicateHeader>
                <Dropdown_ label={t("Apply action to all duplicates")}>
                  <select
                    value={duplicateState.applyToAll ? duplicateState.applyAction : ""}
                    onChange={(e) =>
                      setDuplicateState((prev) => ({
                        ...prev,
                        applyToAll: Boolean(e.target.value),
                        applyAction: e.target.value || prev.applyAction,
                      }))
                    }
                  >
                    <option  value="">{t("No bulk action")}</option>
                    <option value="skip">{t("Skip duplicates")}</option>
                    <option value="update">{t("Update existing")}</option>
                    <option value="keep">{t("Keep both")}</option>
                  </select>
                </Dropdown_>
                <Tip>
                  <IconSvg Icon="Info" />
                  <p>{t("Choose a bulk action or control items per row")}</p>
                </Tip>
              </DuplicateHeader>
              {duplicateState.duplicates.map((dup) => (
                <DuplicateRow key={dup.index}>
                  <div>
                    <h6>{t("CSV row")} #{dup.index + 1}</h6>
                    <SampleValue>
                      <p>
                        {dup.matchedBy}:{" "}
                        {dup.matchedBy === "Email" ? dup.email : dup.tel}
                      </p>
                    </SampleValue>
                  </div>
                  <div>
                    <h6>{t("Existing lead")}</h6>
                    <SampleValue>
                      <p>
                        {dup.existingLead?.FullName ||
                          [dup.existingLead?.Name, dup.existingLead?.Surname].filter(Boolean).join(" ") ||"-"}
                      </p>
                    </SampleValue>
                    {dup.existingLead?.Email ? 
                      <SampleValue>
                        <p>{dup.existingLead?.Email || "-"}</p>
                      </SampleValue>
                    : null}
                    {dup.existingLead?.Tel ? 
                      <SampleValue>
                        <p>{dup.existingLead?.Tel || "-"}</p>
                      </SampleValue>
                    : null}
                  </div>
                  <Dropdown_ label={t("Action")}>
                    <select
                      value={
                        duplicateState.applyToAll
                          ? duplicateState.applyAction
                          : duplicateState.actionByIndex[dup.index] || "skip"
                      }
                      onChange={(e) =>
                        setDuplicateState((prev) => ({
                          ...prev,
                          actionByIndex: {
                            ...prev.actionByIndex,
                            [dup.index]: e.target.value,
                          },
                        }))
                      }
                      disabled={duplicateState.applyToAll}
                    >
                      <option value="skip">{t("Skip duplicates")}</option>
                      <option value="update">{t("Update existing")}</option>
                      <option value="keep">{t("Keep both")}</option>
                    </select>
                  </Dropdown_>
                </DuplicateRow>
              ))}
            </DuplicateSection>
          )}

          {activeStep === 4 && missingCountryRows?.length > 0 && (
            <DuplicateSection>
              <Tip customColor='warning'>
                <IconSvg Icon="Warning" />
                <p>{t("Country code required")}</p>
              </Tip>
              <p>{t("Some phone numbers do not include a country code.")}</p>
              <Builder_>
                <Dropdown_ label={t("Default country for missing codes")}>
                  <select
                    value={defaultImportCountry}
                    onChange={(e) => setDefaultImportCountry(e.target.value)}
                  >
                    <option value="">{t("Select country")}</option>
                    {sortedCountries.map((item, i) => (
                      <option key={i} value={item.phoneCode}>
                        {item.flag} {item.country} {item.phoneCode}
                      </option>
                    ))}
                  </select>
                  
                </Dropdown_>
              </Builder_>
                <Tip>
                  <IconSvg Icon="Tip" />
                  <p> {t( "Accepted formats: +3069..., 003069..., or local numbers (e.g. 69...) with a default country selected." )}</p>
                </Tip>
            </DuplicateSection>
          )}

          <ImportFooter>
            {activeStep !== 4 && rows.length > 0 && (
              <h5>{`${t("Rows detected")} ${rows.length}` }</h5>
            )}
            {importing && (
              <Grey_ text={`${t("Importing")} ${importProgress.done}/${importProgress.total}`} noPadding />
            )}
          </ImportFooter>
        </ImportWrapper>
      )}
    </Slider_>
  );
}
